import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { ProductView } from '@lemoncloud/codes-goods-api';
import { EllipsisVertical } from 'lucide-react';

interface ProductCardActionMenuProps {
    product: ProductView;
    isDeploying?: boolean;
    onSelectResourceDetail?: () => void;
    onSelectEventLogs?: () => void;
    onSelectDeployment?: () => void;
    onDelete?: () => void;
}

export const ProductCardActionMenu = ({
    product,
    isDeploying,
    onSelectResourceDetail,
    onSelectEventLogs,
    onSelectDeployment,
    onDelete,
}: ProductCardActionMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();

    const isApprovedOrDeployed = product.state === 'approved' || product.state === 'deployed';
    const canRedeploy = product.state === 'approved' && product.status !== 'busy' && product.status !== 'idle';
    const isDeployed = product.status === 'running';

    const handleMenuItemClick = (callback?: () => void) => (e: React.MouseEvent) => {
        e.stopPropagation();
        callback?.();
        setIsOpen(false);
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="p-[2px] group w-8 h-8">
                    <EllipsisVertical size={15} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="p-[11px]">
                <DropdownMenuItem className="text-base" onClick={handleMenuItemClick(onSelectResourceDetail)}>
                    {t('serviceSetting.serviceSettings')}
                </DropdownMenuItem>

                {isApprovedOrDeployed && (
                    <>
                        <DropdownMenuItem className="text-base" onClick={handleMenuItemClick(onSelectEventLogs)}>
                            {t('serviceSetting.eventLogs')}
                        </DropdownMenuItem>
                        {canRedeploy && (
                            <DropdownMenuItem
                                className="text-base"
                                onClick={handleMenuItemClick(onSelectDeployment)}
                                disabled={isDeploying}
                            >
                                {t('serviceCard.deploy.reDeploy')}
                            </DropdownMenuItem>
                        )}
                    </>
                )}

                {isDeployed && (
                    <DropdownMenuItem className="text-base" onClick={handleMenuItemClick(onDelete)}>
                        {t('serviceSetting.delete')}
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
